'use client';

import { c as _c } from "react/compiler-runtime";
import { ChartTooltipPanel, FloatingTooltip } from '@/domains/pages/shared/chart-tooltip';
import { pieChartAccentFill } from '@/domains/pages/ai-proficiency/constants';
import { MODEL_SAVINGS_POSITIVE_COLOR, MODEL_SAVINGS_REMAINDER_COLOR } from '@/domains/pages/model-savings/constants';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import { formatAnnualSpend } from '@/domains/pages/model-savings/utils/model-savings.utils';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { ArcElement, Chart as ChartJS, type ActiveElement, type ChartEvent } from 'chart.js';
import { useCallback, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
ChartJS.register(ArcElement);
const ARC_LABELS = ['Savings opportunity', 'Remaining spend'] as const;
type SavingsSharePieChartProps = {
  percent: number;
  size?: number;
  className?: string;
  /** Amounts behind the share, so hovering an arc can name what it stands for. */
  savingAnnual?: number;
  spendAnnual?: number;
};
export function SavingsSharePieChart(t0) {
  const $ = _c(43);
  const {
    percent,
    size: t1,
    className,
    savingAnnual,
    spendAnnual
  } = t0;
  const size = t1 === undefined ? 128 : t1;
  const [hover, setHover] = useState(null);
  const displayPercent = Math.max(0, percent);
  const ringPercent = Math.min(100, displayPercent);
  const remainder = 100 - ringPercent;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [...ARC_LABELS];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== remainder || $[2] !== ringPercent) {
    t3 = [ringPercent, remainder];
    $[1] = remainder;
    $[2] = ringPercent;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [MODEL_SAVINGS_POSITIVE_COLOR, MODEL_SAVINGS_REMAINDER_COLOR];
    t5 = [pieChartAccentFill(MODEL_SAVINGS_POSITIVE_COLOR), pieChartAccentFill(MODEL_SAVINGS_REMAINDER_COLOR)];
    t6 = [`${MODEL_SAVINGS_POSITIVE_COLOR}50`, `${MODEL_SAVINGS_REMAINDER_COLOR}50`];
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t3) {
    t7 = {
      labels: t2,
      datasets: [{
        data: t3,
        borderColor: t4,
        backgroundColor: t5,
        hoverBackgroundColor: t6,
        borderWidth: 1,
        borderRadius: 3
      }]
    };
    $[7] = t3;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const chartData = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (event, elements) => {
      const native = event.native as MouseEvent | null;
      const element = elements[0];
      if (!element || !native) {
        setHover(null);
        return;
      }
      setHover({
        index: element.index,
        x: native.clientX,
        y: native.clientY
      });
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handleHover = t8;
  let t9;
  bb0: {
    if (!hover) {
      let t10;
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = [];
        $[10] = t10;
      } else {
        t10 = $[10];
      }
      t9 = t10;
      break bb0;
    }
    const isSavings = hover.index === 0;
    const share = isSavings ? ringPercent : remainder;
    const amount = isSavings ? savingAnnual : spendAnnual != null && savingAnnual != null ? spendAnnual - savingAnnual : null;
    let t10;
    if ($[11] !== share) {
      t10 = formatScore(share, {
        decimals: 0
      });
      $[11] = share;
      $[12] = t10;
    } else {
      t10 = $[12];
    }
    const t11 = `${t10}%`;
    let t12;
    if ($[13] !== t11) {
      t12 = {
        label: "Share of spend",
        value: t11
      };
      $[13] = t11;
      $[14] = t12;
    } else {
      t12 = $[14];
    }
    let t13;
    if ($[15] !== amount) {
      t13 = amount != null ? [{
        label: "Amount",
        value: formatAnnualSpend(amount)
      }] : [];
      $[15] = amount;
      $[16] = t13;
    } else {
      t13 = $[16];
    }
    let t14;
    if ($[17] !== t12 || $[18] !== t13) {
      t14 = [t12, ...t13];
      $[17] = t12;
      $[18] = t13;
      $[19] = t14;
    } else {
      t14 = $[19];
    }
    t9 = t14;
  }
  const hoverRows = t9;
  let t10;
  if ($[20] !== className) {
    t10 = cn("relative shrink-0", className);
    $[20] = className;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== size) {
    t11 = {
      width: size,
      height: size
    };
    $[22] = size;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setHover(null);
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      cutout: "60%",
      circumference: 360,
      maintainAspectRatio: false,
      onHover: handleHover,
      responsive: true,
      plugins: {
        datalabels: {
          display: false
        },
        legend: {
          display: false
        },
        tooltip: {
          enabled: false
        }
      }
    };
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== chartData) {
    t14 = <Doughnut options={t13} data={chartData} />;
    $[26] = chartData;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn("text-2xl leading-none font-medium tracking-tight", AIFI_DATA_NUMBER_CLASS);
    t16 = {
      color: MODEL_SAVINGS_POSITIVE_COLOR
    };
    $[28] = t15;
    $[29] = t16;
  } else {
    t15 = $[28];
    t16 = $[29];
  }
  let t17;
  if ($[30] !== displayPercent) {
    t17 = formatScore(displayPercent, {
      decimals: 0
    });
    $[30] = displayPercent;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t17) {
    t18 = <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><span className={t15} style={t16}>{t17}%</span></div>;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== hover || $[35] !== hoverRows) {
    t19 = hover ? <FloatingTooltip open={true} x={hover.x} y={hover.y}><ChartTooltipPanel title={ARC_LABELS[hover.index]} rows={hoverRows} /></FloatingTooltip> : null;
    $[34] = hover;
    $[35] = hoverRows;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t14 || $[40] !== t18 || $[41] !== t19) {
    t20 = <div className={t10} style={t11} onMouseLeave={t12}>{t14}{t18}{t19}</div>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t14;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  return t20;
}